import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  CustomNotification,
  StyledModal,
  WithLoading,
  SectionWrapper,
} from 'common/components';
import { Row, Typography, Col, Alert, Form, Button, notification } from 'antd';
import { PostFieldRecursive } from './post-product-form';
import PostProductShortInfo from './common/PostProductShortInfo';

import { green } from '@ant-design/colors';
import { CloseCircleFilled, CheckCircleFilled } from '@ant-design/icons';
import ValidateButton from './common/ValidateButton';

import { useGetWalmartProductSchema } from './queries';

import {
  useFindSchemaForProduct,
  useGetInitialWalmartFormValue,
  useGetWalmartFormErrors,
  useScrollTopAfterRecursiveRender,
  useValidateWalmartFormData,
} from './hooks';

import {
  flattenAndConvert,
  cleanInvalidData,
  formatDatesRecursive,
  removeInvalidValueKey,
} from './post-product-form/utils';

import {
  selectPostToWalmartFormValues,
  setPostToWalmartFormValues,
  updatePostToWalmartFormValues,
} from './slice';

import { sleep } from 'utils/delay';
import ErrorListWithShowMore from './common/ErrorListWithShowMore';

const { Title } = Typography;

const ModalUpdateWalmartForm = ({
  visible,
  postProductItem,
  toggleModalUpdate,
}) => {
  const dispatch = useDispatch();

  const refScrollWrapper = React.useRef(null);
  const [formInstance] = Form.useForm();
  const [isValidatingForm, setIsValidatingForm] = useState(false);

  const productId = postProductItem?.productId;

  const { data: walmartSchemas } = useGetWalmartProductSchema({
    enabled: false,
    productIds: [productId],
  });

  const foundSchema = useFindSchemaForProduct({
    productId,
    walmartSchemas,
  });

  const initialWalmartForm = useGetInitialWalmartFormValue({ productId });

  const walmartFormStringify = JSON.stringify(initialWalmartForm);
  const foundSchemaStringify = JSON.stringify(foundSchema);

  const productPropertySchema = foundSchema?.productProperties;
  const formattedSchema = useMemo(() => {
    return flattenAndConvert(productPropertySchema);
  }, [productPropertySchema]);

  const formattedSchemaStringify = JSON.stringify(formattedSchema);

  const severFormErrorInfo = useGetWalmartFormErrors({
    productId,
  });

  const onCloseModalUpdate = async () => {
    const formValues = formInstance.getFieldsValue();
    const formattedUpdateFormValue = removeInvalidValueKey(formValues);
    dispatch(
      updatePostToWalmartFormValues({
        productId,
        formData: formattedUpdateFormValue,
      })
    );
    await sleep(300);
    toggleModalUpdate();
  };

  useEffect(() => {
    const fillInitialValueToForm = async () => {
      formInstance.resetFields();
      await sleep(300);
      const formattedInitFormValue = removeInvalidValueKey(initialWalmartForm);
      const formattedForm = formatDatesRecursive(
        formattedInitFormValue,
        formattedSchema
      );

      formInstance.setFieldsValue(formattedForm);
    };

    if (foundSchema && initialWalmartForm && formattedSchema) {
      fillInitialValueToForm();
    }
  }, [walmartFormStringify, foundSchemaStringify, formattedSchemaStringify]);

  const { isValid } = severFormErrorInfo || {};

  useScrollTopAfterRecursiveRender(refScrollWrapper, {
    visible,
    schema: foundSchema,
  });

  return (
    <StyledModal
      title={`Edit Walmart product form`}
      visible={visible}
      width={1100}
      onCancel={onCloseModalUpdate}
      okText={'Validate'}
      cancelText={'Close'}
      destroyOnClose
      footer={
        <ModalUpdateWalmartFormFooter
          onCloseModalUpdate={onCloseModalUpdate}
          severFormErrorInfo={severFormErrorInfo}
          formInstance={formInstance}
          productId={productId}
          isValidatingForm={isValidatingForm}
          setIsValidatingForm={setIsValidatingForm}
        />
      }
    >
      <WithLoading loading={isValidatingForm}>
        <div
          ref={refScrollWrapper}
          className='scroller'
          style={{
            minHeight: 'min(60vh, 100px)',
            maxHeight: 'min(65vh, 800px)',
            paddingRight: 6,
          }}
        >
          <SectionWrapper
            title={
              <Title
                level={5}
                type={!isValid ? 'danger' : ''}
                style={{
                  marginBottom: 0,
                  color: isValid ? green[6] : '',
                }}
              >
                {`PRODUCT ID - ${productId}`}{' '}
                {isValid ? <CheckCircleFilled /> : <CloseCircleFilled />}
              </Title>
            }
            containerStyle={{ padding: '20px 12px' }}
          >
            <div style={{ padding: '0 22px', marginBottom: 10 }}>
              <PostProductShortInfo
                postProductItem={postProductItem}
                targetType='walmart'
              />
              {!isValid && (
                <Alert
                  type='error'
                  banner
                  message={
                    <div style={{ marginRight: 100 }}>
                      Please update the values of the fields with errors.
                    </div>
                  }
                  style={{ marginTop: 4 }}
                />
              )}
            </div>

            <PostFieldForm
              formInstance={formInstance}
              postProductItem={postProductItem}
              formSchema={foundSchema}
            />
          </SectionWrapper>
        </div>
      </WithLoading>
    </StyledModal>
  );
};

const PostFieldForm = ({ formInstance, postProductItem, formSchema }) => {
  return (
    <Form
      name={postProductItem?.productId}
      labelCol={{ span: 12 }}
      wrapperCol={{ span: 12 }}
      form={formInstance}
      id='publish-product-walmart-form'
    >
      {formSchema?.['productProperties']?.map((item) => {
        const { key, ...restItemProps } = item;
        return (
          <PostFieldRecursive
            key={item?.fullKey}
            {...restItemProps}
            fieldKey={key}
            productId={postProductItem?.productId}
          />
        );
      })}
    </Form>
  );
};

const ModalUpdateWalmartFormFooter = ({
  onCloseModalUpdate,
  severFormErrorInfo,
  formInstance,
  productId,
  isValidatingForm,
  setIsValidatingForm,
}) => {
  const { handleValidateWalmartProductData } = useValidateWalmartFormData();
  const walmartAllFormValues = useSelector(selectPostToWalmartFormValues);

  const dispatch = useDispatch();

  const onClickValidate = async () => {
    let showErrorNotification = false;
    let validationResult = [];

    try {
      setIsValidatingForm(true);
      const formValues = formInstance.getFieldsValue();

      const foundFormIndex = walmartAllFormValues?.findIndex(
        (formValueItem) => formValueItem?.productId === productId
      );

      if (foundFormIndex === -1) return;

      let newWalmartAllFormValues = [...walmartAllFormValues];

      const selectedFormParams = {
        productId,
        data: formValues,
      };

      newWalmartAllFormValues[foundFormIndex] = selectedFormParams;
      const newWalmartSelectedFormValue = [selectedFormParams];

      const formattedFormValues = cleanInvalidData(newWalmartAllFormValues);
      const formattedSelectedFormValues = cleanInvalidData(
        newWalmartSelectedFormValue
      );
      dispatch(setPostToWalmartFormValues(formattedFormValues));
      await sleep(1500);

      validationResult = await handleValidateWalmartProductData(
        formattedSelectedFormValues
      );

      const hasServerError = validationResult?.some(
        (validationItem) => validationItem?.dataErrors?.length > 0
      );

      if (hasServerError) {
        showErrorNotification = true;
      }

      await formInstance.validateFields();

      if (showErrorNotification) {
        notification.error({
          message: 'Please resolve form errors',
          description: (
            <ErrorListWithShowMore
              errorList={validationResult?.[0]?.dataErrors}
              fixedShowNumber={5}
            />
          ),
          duration: 6,
          style: { minWidth: showErrorNotification ? 500 : 'unset' },
        });
      } else {
        CustomNotification.success('The product data is valid');
      }

      setIsValidatingForm(false);
    } catch (e) {
      notification.error({
        message: 'Please resolve form errors',
        description: showErrorNotification ? (
          <ErrorListWithShowMore
            errorList={validationResult?.[0]?.dataErrors}
            fixedShowNumber={5}
          />
        ) : null,
        duration: 6,
        style: { minWidth: showErrorNotification ? 500 : 'unset' },
      });
      setIsValidatingForm(false);
    }
  };

  const formHasError = severFormErrorInfo?.dataErrors?.length > 0;

  return (
    <Row>
      <Col>
        <ValidateButton
          onClick={() => onClickValidate()}
          hasError={formHasError}
          loading={isValidatingForm}
        />
      </Col>
      <Col flex='auto'></Col>
      <Col>
        <Button onClick={onCloseModalUpdate}>Close</Button>
      </Col>
    </Row>
  );
};

export default ModalUpdateWalmartForm;
